import { Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { usersData$, userRefreshing$ } from "../reduxStore/selectors/userSelectors";
import { refreshUser } from "../reduxStore/actions/userActionsHelper";
import { getAccessToken } from "../api/config";
import SessionValidationOverlay from "../components/SessionValidationOverlay/SessionValidationOverlay";
import { hasRole } from "../utils/Enum/RoleEnum";
import { USER_ROUTE_ACCESSIBLE_ROLES } from "../utils/roleAccess";
import LoadingSpinner from "../components/loadingSpinner/loadingSpinner";
import MessageConstant from "../utils/Constant/MessageConstant";

const IS_DEMO = process.env.REACT_APP_ENVCONFIG === 'demo';

export default function UserRoute({ children }) {
    const dispatch = useDispatch();
    const token = getAccessToken();
    const userData = useSelector(usersData$);
    const isRefreshing = useSelector(userRefreshing$);
    const [showOverlay, setShowOverlay] = useState(false);
    const [isAuthInitialized, setIsAuthInitialized] = useState(false);
    const [hasAttemptedRefresh, setHasAttemptedRefresh] = useState(false);

    // Check if user has access to user routes (user or admin roles)
    const hasAccess = hasRole(userData, USER_ROUTE_ACCESSIBLE_ROLES);

    // Initialize authentication on component mount (restore session after page refresh)
    useEffect(() => {
        if (IS_DEMO) {
            setHasAttemptedRefresh(true);
            setIsAuthInitialized(true);
            return;
        }

        if (!hasAttemptedRefresh) {
            setHasAttemptedRefresh(true);
            // Always attempt refresh on mount - httpOnly cookie might exist even if in-memory token is null (e.g., after F5)
            dispatch(refreshUser.request({ retryCount: 0, maxRetries: 4 }));
        }
    }, [dispatch, hasAttemptedRefresh]);

    // Mark auth as initialized after refresh completes (success or failure)
    useEffect(() => {
        if (hasAttemptedRefresh && !isRefreshing) {
            setIsAuthInitialized(true);
        }
    }, [hasAttemptedRefresh, isRefreshing]);

    useEffect(() => {
        // Listen for session invalidation events
        const handleSessionInvalid = () => {
            setShowOverlay(true);
        };

        window.addEventListener('session-invalid', handleSessionInvalid);

        return () => {
            window.removeEventListener('session-invalid', handleSessionInvalid);
        };
    }, []);

    // Monitor token and user data changes
    useEffect(() => {
        if (token && userData && hasAccess) {
            setShowOverlay(false);
        } else if (!token || !userData) {
            // Token or user data is missing during runtime
            setShowOverlay(true);
        }
    }, [token, userData, hasAccess]);

    // Don't render routes until auth check is complete
    if (!isAuthInitialized) {
        return <LoadingSpinner message={MessageConstant.INITIALIZING} />;
    }

    // Check if user has access to user routes (user or admin)
    // In demo mode, DemoRoute is the auth guard — defer unauthenticated cases to it (no redirect to sign-in).
    if (!token || !userData || !hasAccess) {
        if (IS_DEMO) return null; // DemoRoute will show the onboarding modal
        return <Navigate to="/sign-in" replace />;
    }

    return (
        <>
            {showOverlay && <SessionValidationOverlay />}
            <div style={{ pointerEvents: showOverlay ? 'none' : 'auto' }}>
                {children}
            </div>
        </>
    );
}